import { useRef, useState } from 'preact/hooks'
import { Button } from '~/components/primitives/Button'
import { InfoSheet } from '~/components/primitives/InfoSheet'
import {
  DEFAULT_PRIVACY_SELECTION,
  type ObservationScope,
  type PrivacySelection,
} from '~/exports/scope'
import { preloadPdfRenderer } from '~/services/exportService'
import styles from './ExportControls.module.css'

/**
 * "Export for AI Review" (D-008, D-019, D-021), behind one button (D-104 §5).
 *
 * Every toggle starts in the private position and the scope starts at the
 * current week plus the previous four. The user opts INTO disclosure, never out
 * of it, and the confirmation states plainly that the file leaves Project
 * Sola's control — because once it is shared, it cannot be recalled.
 *
 * **Photos gained a toggle at Checkpoint 4**, because they now exist. Strength
 * screenshots still have none — nothing captures one, so a switch that could not
 * change anything would imply a capability the app does not have, and the export
 * records them as excluded regardless.
 *
 * **One format at a time, deliberately** (D-082). Offering "both" would hand iOS
 * two Share sheets in succession, and the second arrives after the user believes
 * the task is finished. The two files carry the same meaning, so choosing is
 * cheap.
 *
 * ────────────────────────────────────────────────────────────────────────────
 *  FIFTEEN CONTROLS MOVED INTO A SHEET, AND THE WARNING DID NOT MOVE WITH THEM
 * ────────────────────────────────────────────────────────────────────────────
 *
 * C7.4 phase B replaced a permanently-open form with one button. **The D-021
 * warning is still immediately above the button that creates the file, and is
 * never nested inside a disclosure** (D-104 §5): D-083's reasoning is that a
 * warning arriving with the finished artifact is a notification rather than a
 * choice, and a warning the user has to open a section to read is the same
 * failure one step earlier.
 *
 * **The sheet is dismissed by Escape, Close or the scrim, and none of them
 * exports anything.** While a file is being built every dismissal path is
 * blocked, so a mis-tap cannot close the sheet out from under a write.
 *
 * **This is rendered by the page, never by a glass surface.** `check-boundaries`
 * enforces it — see `InfoSheet` for why.
 */
export type ExportFormat = 'json' | 'pdf' | 'csv' | 'media'

interface ExportControlsProps {
  readonly onExport: (selection: PrivacySelection, format: ExportFormat) => Promise<void>
}

const FORMAT_OPTIONS: readonly { value: ExportFormat; label: string; hint: string }[] = [
  { value: 'json', label: 'JSON', hint: 'The format to upload to ChatGPT.' },
  { value: 'pdf', label: 'PDF', hint: 'To read on your phone or print.' },
  { value: 'csv', label: 'Spreadsheet', hint: 'One CSV table per kind of record.' },
  { value: 'media', label: 'Photo ZIP', hint: 'Your original photos, with a manifest.' },
]

const SCOPE_OPTIONS: readonly { value: ObservationScope; label: string }[] = [
  { value: 'currentWeek', label: 'This week only' },
  { value: 'currentPlusPreviousFour', label: 'This week and the previous four' },
  { value: 'fullHistory', label: 'Everything recorded' },
]

export function ExportControls({ onExport }: ExportControlsProps) {
  const [selection, setSelection] = useState<PrivacySelection>(DEFAULT_PRIVACY_SELECTION)
  const [format, setFormat] = useState<ExportFormat>('json')
  const [open, setOpen] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | undefined>(undefined)

  /* Supplied to the sheet rather than inferred: tapping a button does not focus
     it in iOS Safari, so `document.activeElement` would be `<body>` on the one
     platform this app is built for. */
  const openerRef = useRef<HTMLButtonElement | null>(null)

  const run = async () => {
    setBusy(true)
    setError(undefined)
    try {
      await onExport(selection, format)
      setOpen(false)
    } catch (cause: unknown) {
      setError(cause instanceof Error ? cause.message : String(cause))
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      {/*
        Secondary, and that is the §9 criterion rather than a taste call: the
        finished review carries exactly one primary-styled action, and it is the
        second opinion above this. Both controls ask someone else for a view;
        the one the app can actually carry out leads.
      */}
      <Button
        variant="secondary"
        fullWidth
        elementRef={(element) => {
          openerRef.current = element
        }}
        onClick={() => {
          setError(undefined)
          setOpen(true)
        }}
      >
        Export for AI review
      </Button>

      <InfoSheet
        open={open}
        title="Export for AI review"
        busy={busy}
        returnFocusTo={openerRef}
        onClose={() => setOpen(false)}
      >
        <p class={styles.body}>
          Creates a file on this device containing Project Sola's own opinion first, then asks
          ChatGPT to critique it. Nothing is uploaded — you choose what to do with the file.
        </p>

        <fieldset class={styles.group}>
          <legend class={styles.legend}>File type</legend>
          {FORMAT_OPTIONS.map((option) => (
            <label key={option.value} class={styles.choice}>
              <input
                type="radio"
                name="export-format"
                value={option.value}
                checked={format === option.value}
                onChange={() => {
                  setFormat(option.value)
                  /* Warm the 122 kB PDF chunk now, so the Share sheet is called
                     with the user activation the tap provides rather than after
                     an import has spent it (D-085). */
                  if (option.value === 'pdf') preloadPdfRenderer()
                }}
              />
              <span>
                {option.label} — {option.hint}
              </span>
            </label>
          ))}
        </fieldset>

        <fieldset class={styles.group}>
          <legend class={styles.legend}>How much history to include</legend>
          {SCOPE_OPTIONS.map((option) => (
            <label key={option.value} class={styles.choice}>
              <input
                type="radio"
                name="export-scope"
                value={option.value}
                checked={selection.scope === option.value}
                onChange={() => setSelection((current) => ({ ...current, scope: option.value }))}
              />
              <span>{option.label}</span>
            </label>
          ))}
        </fieldset>

        {/*
          Not an `OptionalSection`. The three private details are off by default
          and must be *seen* to be off — a collapsed group would hide the fact
          that photos, notes and exact dates are excluded, and D-021's opt-in is
          only informed if the user can see what they are opting into.
        */}
        <fieldset class={styles.group}>
          <legend class={styles.legend}>Optional details</legend>
          <p class={styles.hint}>All three are left out unless you turn them on.</p>

          <label class={styles.choice}>
            <input
              type="checkbox"
              checked={selection.includePhotos}
              onChange={(event) =>
                setSelection((current) => ({
                  ...current,
                  includePhotos: (event.currentTarget as HTMLInputElement).checked,
                }))
              }
            />
            <span>Include my progress photos</span>
          </label>

          <label class={styles.choice}>
            <input
              type="checkbox"
              checked={selection.includeNotes}
              onChange={(event) =>
                setSelection((current) => ({
                  ...current,
                  includeNotes: (event.currentTarget as HTMLInputElement).checked,
                }))
              }
            />
            <span>Include my personal notes</span>
          </label>

          <label class={styles.choice}>
            <input
              type="checkbox"
              checked={selection.includeExactDates}
              onChange={(event) =>
                setSelection((current) => ({
                  ...current,
                  includeExactDates: (event.currentTarget as HTMLInputElement).checked,
                }))
              }
            />
            <span>Include exact dates</span>
          </label>
        </fieldset>

        <div class={styles.confirm}>
          <p class={styles.body}>
            This file will contain your body measurements and health-related information
            {selection.includePhotos ? ', including your progress photos' : ''}
            {selection.includeNotes ? ', including your personal notes' : ''}
            {selection.includeExactDates ? ', including exact dates' : ''}.
          </p>
          {format === 'media' && !selection.includePhotos ? (
            <p class={styles.body}>
              Turn on <strong>Include my progress photos</strong> first — there is nothing to put in
              a photo file otherwise.
            </p>
          ) : null}
          <p class={styles.warning}>
            Once you save or share it, the file is outside Project Sola's control. It cannot be
            recalled, deleted, or protected afterwards.
          </p>
          <Button onClick={() => void run()} busy={busy} busyLabel="Creating…" fullWidth>
            Create the file
          </Button>
        </div>

        {error === undefined ? null : (
          <p class={styles.error} role="alert">
            The export could not be created. {error}
          </p>
        )}
      </InfoSheet>
    </>
  )
}
